import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Chip, DataTable, Modal } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { formatDateTime } from "utils/time";

import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSOffersQuery } from "state/communities/communitiesApiSlice";

const DIRECTION_ICON = { UP: "arrow_upward", DOWN: "arrow_downward" };
const DIRECTION_TONE = { UP: "green", DOWN: "amber" };

const STATUS_TONE = {
  PLACED: "gray",
  SELECTED: "info",
  ACCEPTED: "green",
  REJECTED: "red",
  ACTIVE: "amber",
  COMPLETED: "gray",
};

function OffersTable({ moduleType, startDatetime, endDatetime }) {
  const { t } = useTranslation(["community", "common"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const {
    data: offers,
    isFetching,
    currentData,
  } = useGetEMSOffersQuery(
    { communityId, moduleType, startDatetime, endDatetime },
    { skip: !communityId, pollingInterval: 30000 }
  );
  // Pending for the *current* args (date range / module); a background
  // polling refetch keeps currentData and therefore does not flash a spinner.
  const isLoading = isFetching && currentData === undefined;

  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  const data = useMemo(() => offers || [], [offers]);

  const rows = useMemo(
    () =>
      data.map((offer, i) => ({
        id: offer.external_offer_id ?? `${offer.start_datetime}-${i}`,
        start: formatDateTime(offer.start_datetime),
        end: formatDateTime(offer.end_datetime),
        direction: offer.direction,
        quantity: offer.quantity,
        price: offer.activation_price,
        status: offer.status,
        raw: offer,
      })),
    [data]
  );

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  const statusChip = (status) => (
    <Chip tone={STATUS_TONE[status] || "gray"}>
      {t(`unified_ems.status_${String(status).toLowerCase()}`, { defaultValue: status })}
    </Chip>
  );

  const directionChip = (direction) =>
    direction ? (
      <Chip tone={DIRECTION_TONE[direction] || "gray"}>
        <span className="inline-flex items-center gap-1">
          {DIRECTION_ICON[direction] && (
            <Icon name={DIRECTION_ICON[direction]} style={{ fontSize: 14 }} />
          )}
          {t(`unified_ems.direction_${String(direction).toLowerCase()}`, {
            defaultValue: direction,
          })}
        </span>
      </Chip>
    ) : (
      "—"
    );

  const columns = [
    {
      key: "start",
      label: t("unified_ems.start_datetime"),
      sortValue: (row) => new Date(row.raw?.start_datetime).getTime() || 0,
    },
    {
      key: "end",
      label: t("unified_ems.end_datetime"),
      sortValue: (row) => new Date(row.raw?.end_datetime).getTime() || 0,
    },
    { key: "direction", label: t("unified_ems.direction"), render: (v) => directionChip(v) },
    {
      key: "quantity",
      label: t("unified_ems.quantity"),
      align: "right",
      render: (v) => <span className="tabular-nums">{`${Number(v).toFixed(2)} kW`}</span>,
    },
    {
      key: "price",
      label: t("unified_ems.price"),
      align: "right",
      render: (v) => <span className="tabular-nums">{`${Number(v).toFixed(2)} €/kWh`}</span>,
    },
    { key: "status", label: t("common:status"), render: (v) => statusChip(v) },
    {
      key: "actions",
      label: "",
      align: "right",
      sort: false,
      render: (_v, row) => (
        <button
          type="button"
          className="rec-icon-btn !w-[34px] !h-[34px]"
          title={t("unified_ems.offer_details")}
          onClick={() => setSelected(row.raw)}
        >
          <Icon name="visibility" style={{ fontSize: 18 }} />
        </button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-10 text-ink-3">
        <span className="animate-spin inline-flex">
          <Icon name="progress_activity" style={{ fontSize: 30, color: "var(--accent)" }} />
        </span>
      </div>
    );
  }

  const IdField = ({ label, value }) => (
    <div>
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.4px] text-ink-3 mb-1">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12.5px] text-ink break-all">{value || "—"}</span>
        {value && (
          <button
            type="button"
            className="rec-icon-btn !w-[28px] !h-[28px] shrink-0"
            title={t("common:copy")}
            onClick={() => copy(value)}
          >
            <Icon name="content_copy" style={{ fontSize: 15 }} />
          </button>
        )}
      </div>
    </div>
  );
  IdField.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string };
  IdField.defaultProps = { value: null };

  const kv = (label, node) =>
    node == null || node === "" ? null : (
      <div>
        <div className="text-[11.5px] font-semibold uppercase tracking-[0.4px] text-ink-3 mb-1">
          {label}
        </div>
        <div className="text-[13.5px] text-ink">{node}</div>
      </div>
    );

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        search={t("common:search", { defaultValue: "Procurar…" })}
        searchKeys={["start", "end", "status", "direction"]}
        maxRows={10}
        emptyText={t("unified_ems.no_offers_submitted")}
      />
      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={t("unified_ems.offer_details")}
        subtitle={copied ? t("common:copied") : undefined}
        maxWidth={640}
        footer={
          <button type="button" className="rec-btn-ghost" onClick={() => setSelected(null)}>
            {t("common:close")}
          </button>
        }
      >
        {selected && (
          <div className="flex flex-col gap-[14px]">
            <div className="flex flex-col gap-[12px]">
              <IdField
                label={t("unified_ems.external_offer_id")}
                value={selected.external_offer_id}
              />
              <IdField label="Flexibility Needs ID" value={selected.flexibility_needs_id} />
            </div>
            <div className="rec-divider" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
              {kv(t("common:status"), statusChip(selected.status))}
              {kv(t("unified_ems.start_datetime"), formatDateTime(selected.start_datetime))}
              {kv(t("unified_ems.end_datetime"), formatDateTime(selected.end_datetime))}
              {kv(t("unified_ems.direction"), directionChip(selected.direction))}
              {kv(t("unified_ems.quantity"), `${selected.quantity.toFixed(2)} kW`)}
              {kv(
                t("unified_ems.activation_price"),
                `${selected.activation_price.toFixed(2)} €/kWh`
              )}
              {selected.reservation_price != null &&
                kv(
                  t("unified_ems.reservation_price"),
                  `${selected.reservation_price.toFixed(2)} €/kWh`
                )}
              {selected.recovery_time != null &&
                kv(
                  t("unified_ems.recovery_time"),
                  `${selected.recovery_time} ${t("unified_ems.hours")}`
                )}
              {selected.max_number_activations != null &&
                kv(t("unified_ems.max_activations"), String(selected.max_number_activations))}
              {kv(t("unified_ems.created_at"), formatDateTime(selected.created_at))}
              {kv(t("unified_ems.updated_at"), formatDateTime(selected.updated_at))}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

OffersTable.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
};

OffersTable.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
};

export default OffersTable;
